/*
This file contains a simple react component that handles the Business Central connect
entry point once the visitor is authenticated (see businessCentralConnectUtils.ts and
useBusinessCentralConnect.ts).

Unlike e-conomic, Business Central has no static token exchange - completing the
connection requires an OAuth redirect to Microsoft. We call the existing authenticated
`/v1/integrations/business_central/authorize/` endpoint with any preserved tenant/environment
hint and redirect the browser to the returned consent URL.
*/
import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  InputLabel,
  Typography,
} from "@mui/material";
import { Trans } from "@lingui/react/macro";
import axios from "../../utils/axios";
import {
  clearBusinessCentralHint,
  getStoredBusinessCentralHint,
} from "./businessCentralConnectUtils";

type ConnectPhase = "connecting" | "error";

const BusinessCentralConnect = () => {
  const [phase, setPhase] = useState<ConnectPhase>("connecting");

  useEffect(() => {
    const { tenantId, environment } = getStoredBusinessCentralHint();

    const authorize = async () => {
      try {
        const response = await axios.get<{ authorize_url?: string }>(
          "/v1/integrations/business_central/authorize/",
          {
            params: {
              bc_tenant_id: tenantId ?? undefined,
              environment: environment ?? undefined,
            },
          },
        );
        const authorizeUrl = response.data.authorize_url;
        if (authorizeUrl) {
          clearBusinessCentralHint();
          window.location.href = authorizeUrl;
          return;
        }
        setPhase("error");
      } catch (error) {
        console.error(
          "Failed to get Business Central authorization URL:",
          error,
        );
        setPhase("error");
      }
    };

    authorize();
  }, []);

  return (
    <Box sx={{ bgcolor: "secondary.main" }}>
      <Grid container direction="row" spacing={3} alignContent="center">
        <Grid size={{ xs: 12, lg: 12 }}>
          <InputLabel sx={{ marginBottom: "3%", marginTop: "4%" }}>
            <Trans context="Business Central connect page heading">
              Connecting to Business Central
            </Trans>
          </InputLabel>
          {phase === "connecting" && <CircularProgress />}
          <Typography sx={{ marginTop: "1.5%", marginBottom: "1.5%" }}>
            {phase === "error" ? (
              <span style={{ color: "red" }}>
                <Trans context="Business Central connect page - error message">
                  Failed to connect. Please try again.
                </Trans>
              </span>
            ) : (
              <Trans context="Business Central connect page - connecting in progress message">
                Connecting...
              </Trans>
            )}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BusinessCentralConnect;
